import React, { useState, useEffect, useRef } from "react";
import { useSlideContext } from "../../../../../contexts/slideContext";

import {
  Box,
  Paper,
  Button,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from "@mui/material";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { Row, Col } from "react-bootstrap";
import interact from "interactjs";

export default function BlockTree() {
  const [activeSlideBlocks, setActiveSlideBlocks] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const {
    currentEditorInstanceRef,
    currentSlideRef,
    registerBlockTreeUpdateFunction,
    triggerBlockOrder,
    showBlockTree,
    setShowBlockTree,
    activeSlideLayout,
    setHoveredNode,
  } = useSlideContext();

  const listRef = useRef(null);
  const draggedElRef = useRef(null);
  const placeholderRef = useRef(null);
  const mainContainerRef = useRef(null);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowBlockTree(false);
      setIsClosing(false);
      // Reset position after close
      if (mainContainerRef.current) {
        const el = mainContainerRef.current;
        el.style.left = "";
        el.style.right = "";
        el.style.top = "";
        delete el.dataset.dragX;
        delete el.dataset.dragY;
      }
    }, 300);
  };

  // Load blocks
  useEffect(() => {
    if (
      currentEditorInstanceRef.current &&
      currentSlideRef.current &&
      currentEditorInstanceRef.current?.blocks
    ) {
      registerBlockTreeUpdateFunction(runUpdateActiveSlideBlocks);
      runUpdateActiveSlideBlocks();
    } else if (
      currentEditorInstanceRef.current === null &&
      currentSlideRef.current === null
    ) {
      setActiveSlideBlocks([]);
    }
    // Note: refs and functions are stable references from context; .current values
    // don't trigger re-renders, so this effect relies on external re-render triggers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEditorInstanceRef.current, currentSlideRef.current]);

  // Triggers changes done on slide
  const runUpdateActiveSlideBlocks = async () => {
    const editor = currentEditorInstanceRef.current;
    const slide = currentSlideRef.current;
    if (!editor || !slide) return;

    const blocks = editor.blocks;
    const blockLength = blocks?.getBlocksCount() ?? 0;

    const ids = await Promise.all(
      Array.from({ length: blockLength }, async (_, i) => {
        const block = blocks.getBlockByIndex(i);
        if (!block?.id) return null;

        let toolboxEntry = null;
        try {
          toolboxEntry = await block.getActiveToolboxEntry(block.name);
        } catch {}

        const foundBlock = slide.querySelector(
          `.ce-block[data-id="${block.id}"]`,
        );

        return {
          id: block.id,
          name: block.name,
          toolboxLabel: toolboxEntry?.title || toolboxEntry?.name || block.name,
          toolboxIcon: toolboxEntry?.icon || null,
          groupId: foundBlock?.getAttribute("data-group") ?? null,
        };
      }),
    );

    normalizeGroupedBlocks(ids, editor);
    setActiveSlideBlocks(ids.filter(Boolean));
  };

  const normalizeGroupedBlocks = (_blocks, editor) => {
    const groupIds = [
      ...new Set(_blocks.map((block) => block.groupId).filter(Boolean)),
    ];

    for (const groupId of groupIds) {
      const groupedBlocks = _blocks.filter((b) => b.groupId === groupId);

      let startingIndex;

      const reversed = [...groupedBlocks].reverse();

      for (let index = 0; index < reversed.length; index++) {
        const groupedBlock = reversed[index];
        const blockIndex = _blocks.findIndex(
          (_block) => _block.id === groupedBlock.id,
        );

        if (index === 0) {
          startingIndex = blockIndex;
          continue;
        }

        if (startingIndex - index !== blockIndex) {
          editor.blocks.move(startingIndex - index, blockIndex);

          break;
        }
      }
    }
  };

  const processDragEnd = () => {
    const blocks = [...listRef.current.querySelectorAll(".block-tree-node")];
    const blockIds = blocks.map((b) => b.dataset.id).reverse();
    setIsDragging(false);
    triggerBlockOrder(blockIds);
  };

  // Setup interact.js draggable behavior
  useEffect(() => {
    interact(".draggable").draggable({
      // inertia: true,
      // autoScroll: true,
      listeners: {
        start(event) {
          const el = event.target;
          draggedElRef.current = el;

          const rect = el.getBoundingClientRect();
          let listRect, left, top;
          if (!el.classList.contains("grouped")) {
            listRect = listRef.current.getBoundingClientRect();

            left = rect.left - listRect.left + listRef.current.scrollLeft;
            top = rect.top - listRect.top + listRef.current.scrollTop;
          } else {
            const groupNodeList = el.closest(".group-node-list");
            listRect = groupNodeList.getBoundingClientRect();

            left = rect.left - listRect.left + groupNodeList.scrollLeft;
            top = rect.top - listRect.top + groupNodeList.scrollTop;
          }

          // Adjust rect to be relative to the list container

          // Create placeholder
          const placeholder = document.createElement("div");
          placeholder.className = "placeholder";
          placeholder.style.height = `${rect.height}px`;
          placeholderRef.current = placeholder;

          el.parentNode.insertBefore(placeholder, el.nextSibling);

          el.classList.add("dragging");
          el.style.width = `${rect.width}px`;
          el.style.left = `${left}px`;
          el.style.top = `${top}px`;
          el.style.position = "absolute";

          el.dataset.x = left;
          el.dataset.y = top;
        },

        move(event) {
          const el = draggedElRef.current;
          if (!el) return;

          let x = parseFloat(el.dataset.x) + event.dx;
          let y = parseFloat(el.dataset.y) + event.dy;

          el.style.left = `${x}px`;
          el.style.top = `${y}px`;
          el.dataset.x = x;
          el.dataset.y = y;
          const draggedRect = el.getBoundingClientRect();
          let list, children;
          if (!el.classList.contains("grouped")) {
            list = listRef.current;
            children = [
              ...list.querySelectorAll(".draggable:not(.dragging, .grouped)"),
            ];
          } else {
            list = el.closest(".group-node-list");

            children = [...list.querySelectorAll(".draggable:not(.dragging)")];
          }

          for (const child of children) {
            const rect = child.getBoundingClientRect();
            const midpoint = rect.top + rect.height / 2;

            if (draggedRect.top < midpoint) {
              list.insertBefore(placeholderRef.current, child);
              return;
            }
          }

          list.appendChild(placeholderRef.current);
        },

        end() {
          const el = draggedElRef.current;
          const placeholder = placeholderRef.current;
          const list = el.classList.contains("grouped")
            ? el.closest(".group-node-list")
            : listRef.current;

          if (!el || !placeholder) return;

          list.insertBefore(el, placeholder);

          el.classList.remove("dragging");
          el.style.left = "";
          el.style.top = "";
          el.style.width = "";
          el.style.position = "";
          delete el.dataset.x;
          delete el.dataset.y;

          placeholder.remove();
          draggedElRef.current = null;
          placeholderRef.current = null;
          if (triggerBlockOrder) {
            triggerBlockOrder("test");
          }
          processDragEnd();
        },
      },
    });

    return () => interact(".draggable").unset();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlideBlocks]);

  // Keep block tree within bounds on window resize
  useEffect(() => {
    const handleResize = () => {
      const el = mainContainerRef.current;
      if (!el || !el.dataset.dragX) return; // Only adjust if element has been moved/resized

      const parent = el.closest(".edit-container");
      if (!parent) return;

      const parentRect = parent.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();

      let x = parseFloat(el.dataset.dragX) || 0;
      let y = parseFloat(el.dataset.dragY) || 0;

      // Constrain to parent bounds
      const maxX = parentRect.width - elRect.width;
      const maxY = parentRect.height - elRect.height;

      x = Math.max(0, Math.min(x, maxX));
      y = Math.max(0, Math.min(y, maxY));

      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
      el.dataset.dragX = x;
      el.dataset.dragY = y;
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Setup draggable and resizable behavior for the main container
  useEffect(() => {
    // Capture ref at effect execution time for cleanup
    const mainContainer = mainContainerRef.current;
    if (!mainContainer) return;

    interact(mainContainer)
      .draggable({
        inertia: false,
        modifiers: [
          interact.modifiers.restrict({
            restriction: ".edit-container",
            endOnly: false,
            elementRect: { top: 0, left: 0, bottom: 1, right: 1 },
          }),
        ],
        listeners: {
          start(event) {
            const el = event.target;

            // Disable transition, text selection, and pointer events during drag
            el.classList.add("container-dragging");
            document.body.style.userSelect = "none";
            document.body.style.pointerEvents = "none";
            el.style.pointerEvents = "auto";

            // Initialize position from bounding rect
            const rect = el.getBoundingClientRect();
            const parentRect = el.offsetParent?.getBoundingClientRect() || {
              left: 0,
              top: 0,
            };

            el.style.right = "auto";
            el.style.transform = "none";
            el.style.left = `${rect.left - parentRect.left}px`;
            el.style.top = `${rect.top - parentRect.top}px`;

            el.dataset.dragX = rect.left - parentRect.left;
            el.dataset.dragY = rect.top - parentRect.top;
          },
          move(event) {
            const el = event.target;
            const x = (parseFloat(el.dataset.dragX) || 0) + event.dx;
            const y = (parseFloat(el.dataset.dragY) || 0) + event.dy;

            el.style.left = `${x}px`;
            el.style.top = `${y}px`;
            el.dataset.dragX = x;
            el.dataset.dragY = y;
          },
          end(event) {
            const el = event.target;
            // Re-enable transition, text selection, and pointer events after drag
            el.classList.remove("container-dragging");
            document.body.style.userSelect = "";
            document.body.style.pointerEvents = "";
            el.style.pointerEvents = "";
            el.style.right = "auto";
          },
        },
      })
      .resizable({
        edges: { left: true, right: true, bottom: true, top: true },
        listeners: {
          start(event) {
            const el = event.target;
            el.classList.add("container-resizing");
            document.body.style.userSelect = "none";

            // Initialize position from bounding rect if not already set (fixes position reset bug)
            if (
              el.dataset.dragX === undefined ||
              el.dataset.dragY === undefined
            ) {
              const rect = el.getBoundingClientRect();
              const parentRect = el.offsetParent?.getBoundingClientRect() || {
                left: 0,
                top: 0,
              };

              el.style.right = "auto";
              el.style.transform = "none";
              el.style.left = `${rect.left - parentRect.left}px`;
              el.style.top = `${rect.top - parentRect.top}px`;

              el.dataset.dragX = rect.left - parentRect.left;
              el.dataset.dragY = rect.top - parentRect.top;
            }
          },
          move(event) {
            const el = event.target;
            let { width, height } = event.rect;
            let { left, top } = event.deltaRect;

            // Update position if resizing from top or left edges
            const x = (parseFloat(el.dataset.dragX) || 0) + left;
            const y = (parseFloat(el.dataset.dragY) || 0) + top;

            el.style.width = `${width}px`;
            el.style.height = `${height}px`;
            el.style.left = `${x}px`;
            el.style.top = `${y}px`;
            el.dataset.dragX = x;
            el.dataset.dragY = y;
          },
          end(event) {
            const el = event.target;
            el.classList.remove("container-resizing");
            document.body.style.userSelect = "";
          },
        },
        modifiers: [
          interact.modifiers.restrictEdges({
            outer: ".edit-container",
          }),
          interact.modifiers.restrictSize({
            min: { width: 200, height: 250 },
            max: { width: 500, height: Infinity },
          }),
        ],
      });

    return () => {
      if (mainContainer) {
        interact(mainContainer).unset();
      }
    };
  }, []);

  const renderBlocks = () => {
    if (activeSlideBlocks.length > 0) {
      const tempActiveSlideBlocks = [...activeSlideBlocks].reverse();
      const orderedBlocks = [];
      const groupMap = {};

      for (const block of tempActiveSlideBlocks) {
        const { groupId } = block;

        if (!groupId) {
          orderedBlocks.push({ renderType: "block", ...block });
          continue;
        }

        if (!groupMap[groupId]) {
          const group = {
            groupId,
            renderType: "group",
            blocks: [],
          };

          groupMap[groupId] = group;
          orderedBlocks.push(group);
        }

        groupMap[groupId].blocks.push(block);
      }

      return (
        <>
          {orderedBlocks.map((element) =>
            element?.renderType === "group"
              ? GroupNode(element)
              : BlockNode(element),
          )}
        </>
      );
    }
  };

  const GroupNode = (blockGroup) => {
    return (
      <Accordion
        key={blockGroup.groupId}
        className="draggable group-node"
        disableGutters
        elevation={0}
        onMouseEnter={() => {
          if (isDragging) return;
          setHoveredNode({ type: "group", id: blockGroup.groupId });
        }}
        onMouseLeave={() => {
          if (isDragging) return;
          setHoveredNode(null);
          setIsDragging(false);
        }}
        onPointerDown={() => setIsDragging(true)}
        onPointerUp={() => setIsDragging(false)}
      >
        <AccordionSummary
          expandIcon={<ChevronLeftIcon />}
          sx={{
            "& .MuiAccordionSummary-expandIconWrapper": {
              transform: "rotate(0deg)",
            },
            "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
              transform: "rotate(-90deg)",
            },
          }}
        >
          <Row className="d-flex align-items-center">
            <Col xs="auto" className="px-1">
              <div className="block-tree-node-icon">
                <WorkspacesOutlinedIcon sx={{ fontSize: 20 }} />
              </div>
            </Col>
            <Col className="flex-grow-1 ps-0">
              <Typography
                variant="caption"
                owner={"admin"}
                color={"clientText.dark"}
                weight="medium"
              >
                Group
              </Typography>
            </Col>
          </Row>
        </AccordionSummary>
        <AccordionDetails className="group-node-list">
          {blockGroup.blocks.map((block) => BlockNode(block, true))}
        </AccordionDetails>
      </Accordion>
    );
  };

  const BlockNode = (block, grouped) => {
    return (
      <Box
        className={`block-tree-node draggable ${grouped ? "grouped" : ""}`}
        key={block.id}
        data-id={block.id}
        // data-index={_index}
        onMouseEnter={() => {
          if (isDragging) return;
          setHoveredNode({ type: "block", id: block.id });
        }}
        onMouseLeave={() => {
          if (isDragging) return;
          setHoveredNode(null);
          setIsDragging(false);
        }}
        onPointerDown={() => setIsDragging(true)}
        onPointerUp={() => setIsDragging(false)}
      >
        <Row>
          <Col xs="auto" className="px-1">
            <div
              className="block-tree-node-icon"
              dangerouslySetInnerHTML={{
                __html: block.toolboxIcon || "",
              }}
            />
          </Col>
          <Col className="flex-grow-1 ps-0">
            <Typography
              variant="caption"
              owner={"admin"}
              color={"clientText.dark"}
              weight="medium"
            >
              {block.toolboxLabel ?? block.name}{" "}
            </Typography>
          </Col>
        </Row>
      </Box>
    );
  };

  return (
    <Box
      ref={mainContainerRef}
      className={`block-tree-main-container ${showBlockTree && activeSlideLayout === "custom" ? "expanded" : ""} ${isClosing ? "closing" : ""}`}
      sx={{
        height: 768,
        maxWidth: 500,
        minWidth: 200,
        minHeight: 250,
        maxHeight: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Paper
        className="block-tree-paper-container"
        elevation={3}
        sx={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        <Box
          className="block-tree-inner-container"
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Stack
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            className="block-tree-header"
            spacing={1}
          >
            <Typography
              className="my-2"
              variant="h5"
              owner="admin"
              color="adminText.dark"
              lh="medium"
            >
              Block Tree
            </Typography>

            <Tooltip title={"Hide Block Tree"} placement="bottom">
              <Button
                variant="text"
                className="block-tree-toggle-btn"
                onClick={handleClose}
              >
                <CloseIcon sx={{ fontSize: 24 }} />
              </Button>
            </Tooltip>
          </Stack>
          <Box
            className="block-tree-list-container"
            ref={listRef}
            sx={{ flex: 1, overflowY: "auto" }}
          >
            {activeSlideBlocks?.length > 0 ? (
              renderBlocks()
            ) : (
              <Typography
                variant="body2"
                sx={{ padding: "10px", color: "#777" }}
              >
                No blocks available
              </Typography>
            )}
            {/* {activeSlideBlocks.length > 0 ? (
              [...activeSlideBlocks]
                .reverse()
                .map((block, index) => BlockNode(block, index))
            ) : (
              <Typography
                variant="body2"
                sx={{ padding: "10px", color: "#777" }}
              >
                No blocks available
              </Typography>
            )} */}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
